// `/onboarding/{co}/{token}` (ONBOARDING-P1): the client's owner fills the onboarding intake by the
// link they were sent, with no account (the token is the key, like a client survey). One step at a
// time: save and resume, then Submit once every required answer is in. Client-facing: it speaks as
// the HTVA team's setup assistant and names no internal agent.
// ONBOARDING-P3: a progress bar and the list of steps (saved ones ticked), a clear saved state and
// plain errors; once the intake is in, the setup proposal to review (proposal-review.tsx).
import { ONBOARDING_INTAKE, type StepDef } from "@companyos/domain-work/flows";
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  BrandMark,
  Button,
  cn,
  EmptyState,
  Input,
  Label,
  Progress,
  Spinner,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CircleCheckIcon, LinkIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import { ProposalReview } from "./proposal-review";

type View = OperationOutput<"onboarding.intake.get">;

const intakeKey = (co: string, token: string) => ["onboarding-intake", co, token] as const;
const STEPS: StepDef[] = ONBOARDING_INTAKE.chapters.flatMap((c) => [...c.steps]);

function Frame({ children, company }: { children: ReactNode; company?: string }) {
  return (
    <main
      id="main"
      className="min-h-svh bg-background px-4 py-8 text-foreground max-md:py-5"
      data-slot="onboarding-intake-page"
    >
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5">
        <div className="flex items-center gap-2.5" data-slot="wordmark">
          <BrandMark name={company ?? "CompanyOS"} size={32} />
          <span className="truncate font-heading text-base font-strong">
            {company ?? "CompanyOS"}
          </span>
        </div>
        {children}
      </div>
    </main>
  );
}

export function OnboardingIntakePage({ api, co, token }: { api: Api; co: string; token: string }) {
  const view = useQuery({
    queryKey: intakeKey(co, token),
    queryFn: () => api.client.onboarding.intake.get({ params: { co, token } }),
    retry: false,
  });
  if (view.isPending)
    return (
      <Frame>
        <div className="flex justify-center py-16" role="status" aria-label="Loading">
          <Spinner />
        </div>
      </Frame>
    );
  if (view.isError || !view.data) {
    const missing = view.error instanceof ApiError && view.error.status === 404;
    return (
      <Frame>
        <EmptyState
          variant="card"
          icon={LinkIcon}
          title={missing ? "This link doesn't work" : "The page could not be loaded"}
          description={
            missing
              ? "Reply to the email you got and we will send a new link."
              : "Check your connection and reload the page."
          }
        />
      </Frame>
    );
  }
  const v = view.data;
  return (
    <Frame company={v.company_name}>
      {v.status !== "link_sent" && v.status !== "in_progress" ? (
        // ONBOARDING-P3: once the intake is in, the proposal the HTVA team sent (if any).
        <ProposalReview
          api={api}
          co={co}
          token={token}
          fallback={
            <EmptyState
              variant="card"
              icon={CircleCheckIcon}
              title="Thank you, that's everything"
              description={`${v.from} will be in touch about the next step.`}
            />
          }
        />
      ) : (
        <Intake api={api} co={co} token={token} view={v} />
      )}
    </Frame>
  );
}

const asText = (v: unknown) => (Array.isArray(v) ? v.join("\n") : typeof v === "string" ? v : "");

/** A plain sentence for a failed save or submit (never a code or an internal name). */
function saveError(e: unknown, view: View): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return "This link no longer works. Reply to the email you got.";
    if (e.status === 409) return "This was already submitted. Reload the page to see where it is.";
    if (e.status === 422) {
      const missing = view.missing.map((m) => m.label);
      const fields = (e.problem?.errors ?? []).map((x) => x.message);
      if (fields.length) return `Some answers need another look: ${fields.join("; ")}.`;
      if (missing.length) return `Some answers are still missing: ${missing.join(", ")}.`;
      return "Some answers need another look. Check them and try again.";
    }
  }
  return "That could not be saved. Check your connection and try again; your answers are still here.";
}

function StepList({
  at,
  saved,
  onGo,
}: {
  at: number;
  saved: ReadonlySet<string>;
  onGo: (i: number) => void;
}) {
  return (
    <nav aria-label="Steps" data-slot="intake-steps">
      <ol className="flex flex-wrap gap-1.5">
        {STEPS.map((s, i) => {
          const done = saved.has(s.key);
          return (
            <li key={s.key}>
              <button
                type="button"
                onClick={() => onGo(i)}
                aria-current={i === at ? "step" : undefined}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs",
                  i === at
                    ? "border-primary bg-primary-soft text-primary-text"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {done ? <CheckIcon className="size-3" aria-hidden={true} /> : null}
                {s.title}
                {done ? <span className="sr-only"> (saved)</span> : null}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function Intake({ api, co, token, view }: { api: Api; co: string; token: string; view: View }) {
  const qc = useQueryClient();
  const saved = new Set(view.saved_steps);
  const firstOpen = STEPS.findIndex((s) => !saved.has(s.key));
  const [at, setAt] = useState(firstOpen < 0 ? STEPS.length - 1 : firstOpen);
  const step = STEPS[at] as StepDef;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [justSaved, setJustSaved] = useState<string | null>(null);
  const value = (key: string) => draft[key] ?? asText(view.answers[key]);
  const dirty = Object.keys(draft).length > 0;
  const save = useMutation({
    mutationFn: () =>
      api.client.onboarding.intake.saveStep({
        params: { co, token, step: step.key },
        body: {
          values: Object.fromEntries(
            step.fields.map((f) => {
              const raw = value(f.key);
              const list = f.type === "list" || f.type === "links";
              return [
                f.key,
                list
                  ? raw
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean)
                  : raw.trim() || null,
              ];
            }),
          ),
        },
      }),
    onSuccess: (v) => {
      qc.setQueryData(intakeKey(co, token), v);
      setDraft({});
      setJustSaved(step.title);
      if (at < STEPS.length - 1) setAt(at + 1);
    },
  });
  const submit = useMutation({
    mutationFn: () => api.client.onboarding.intake.submit({ params: { co, token } }),
    onSuccess: (v) => qc.setQueryData(intakeKey(co, token), v),
  });
  const go = (i: number) => {
    setAt(i);
    setDraft({});
    setJustSaved(null);
    save.reset();
  };
  const last = at === STEPS.length - 1;
  return (
    <form
      className="flex flex-col gap-5 rounded-lg border border-border bg-card p-5"
      aria-label={ONBOARDING_INTAKE.title}
      onSubmit={(e) => {
        e.preventDefault();
        if (!save.isPending) save.mutate();
      }}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">{view.from}</p>
        <Progress value={view.progress.percent} aria-label="Progress">
          <span className="text-xs text-muted-foreground" data-slot="intake-progress">
            {view.progress.percent}% done · step {at + 1} of {STEPS.length}
          </span>
        </Progress>
        <StepList at={at} saved={saved} onGo={go} />
      </div>
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{step.title}</h1>
        <p className="text-sm text-muted-foreground">{step.info}</p>
      </div>
      {step.fields.map((f) => {
        const id = `intake-${f.key}`;
        const multi = f.type === "longtext" || f.type === "list" || f.type === "links";
        return (
          <div key={f.key} className="flex flex-col gap-1.5">
            <Label htmlFor={id}>
              {f.label}
              {f.required ? "" : " (optional)"}
            </Label>
            <p className="text-xs text-muted-foreground">
              {f.info}
              {f.type === "list" || f.type === "links" ? " One per line." : ""}
            </p>
            {multi ? (
              <Textarea
                id={id}
                rows={4}
                value={value(f.key)}
                onChange={(e) => {
                  setDraft({ ...draft, [f.key]: e.target.value });
                  setJustSaved(null);
                }}
              />
            ) : (
              <Input
                id={id}
                value={value(f.key)}
                placeholder={f.placeholder}
                onChange={(e) => {
                  setDraft({ ...draft, [f.key]: e.target.value });
                  setJustSaved(null);
                }}
              />
            )}
          </div>
        );
      })}
      {save.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {saveError(save.error, view)}
        </p>
      ) : submit.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {saveError(submit.error, view)}
        </p>
      ) : justSaved && !dirty ? (
        <p role="status" className="text-sm text-success-text" data-slot="intake-saved">
          Saved “{justSaved}”. You can close this page and come back with the same link any time.
        </p>
      ) : dirty ? (
        <p className="text-xs text-muted-foreground">Unsaved changes on this step.</p>
      ) : saved.has(step.key) ? (
        <p className="text-xs text-muted-foreground">This step is saved.</p>
      ) : null}
      {last && view.missing.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Before you can submit, we still need: {view.missing.map((m) => m.label).join(", ")}.
        </p>
      ) : null}
      <div className="flex flex-wrap justify-between gap-2">
        <Button type="button" variant="secondary" disabled={at === 0} onClick={() => go(at - 1)}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button type="submit" variant={last ? "secondary" : "default"} disabled={save.isPending}>
            {save.isPending ? "Saving…" : last ? "Save" : "Save and continue"}
          </Button>
          {last ? (
            <Button
              type="button"
              disabled={submit.isPending || dirty || view.missing.length > 0}
              onClick={() => submit.mutate()}
            >
              Submit
            </Button>
          ) : null}
        </div>
      </div>
    </form>
  );
}
